import React from 'react';
import { 
    Users, Shield, Target, Briefcase, Heart, Scale, Lightbulb, 
    Camera, MessageSquare, Clock, Brain, Zap, Activity, AlertTriangle, CheckCircle2,
    Flag, Anchor, Eye, Fingerprint, HeartHandshake, Compass, Megaphone, Coins, Sparkles,
    BookOpen, Landmark
} from "lucide-react";
import { RenderPsychographicSummary } from "../DanvinciHelpers";

// --- AESTHETIC HELPERS ---
const GlassContainer = ({ children, className = "" }) => (
    <div className={`bg-slate-900/40 border border-slate-700/50 rounded-xl overflow-hidden ${className}`}>
        {children}
    </div>
);

export const AudienceTab = ({ psychographics, results, demographicAudit }) => {
    
    // Safety check
    if (!psychographics) return null;

    // --- STRATEGY TRANSLATOR ---
    const getMotivationStrategy = (amygdala, dlpfc) => {
        const safeAmygdala = amygdala || 0.5;
        const safeDlpfc = dlpfc || 0.5;
        const ratio = safeDlpfc / (safeAmygdala || 0.01);
        
        if (ratio > 1.2) {
            return {
                title: "The 'Pragmatic' Voter",
                hook: "Competence & Plans",
                desc: "This audience resists fear-mongering. They engage with detailed plans, competence, and logical arguments. Avoid hyperbole.",
                icon: Compass,
                color: "text-cyan-400",
                bg: "bg-cyan-500/10",
                border: "border-cyan-500/30"
            };
        }
        if (ratio < 0.8) {
            return {
                title: "The 'Visceral' Voter",
                hook: "Security & Belonging",
                desc: "This audience votes on gut instinct and protection. Use strong emotional imagery and focus on threats to their way of life.",
                icon: Shield,
                color: "text-rose-400",
                bg: "bg-rose-500/10",
                border: "border-rose-500/30"
            };
        }
        return {
            title: "The 'Holistic' Voter",
            hook: "Vision + Verification",
            desc: "The 'Goldilocks' zone. They need an emotional hook to pay attention, but a logical reason to commit. Strategy: 'Story first, Stats second.'",
            icon: HeartHandshake,
            color: "text-purple-400",
            bg: "bg-purple-500/10",
            border: "border-purple-500/30"
        };
    };
    
    // --- TRIBAL TRANSLATOR ---
    const getTribalTone = (narcissism, sdo) => {
        const score = ((narcissism || 0.5) + (sdo || 0.5)) / 2;
        if (score > 0.6) return { label: "Defensive & Proud", directive: "Emphasize 'Protecting Our Community' and local pride. Use Us-vs-Them framing." };
        if (score < 0.4) return { label: "Inclusive & Open", directive: "Emphasize broad coalitions, openness, and 'Winning Together'. Avoid divisive rhetoric." };
        return { label: "Community-Focused", directive: "Focus on local stability and civic duty. They care about their neighbors but aren't hostile to outsiders." };
    };

    // --- ECONOMIC & PERSONALITY TRANSLATOR ---
    const getEconomicDriver = (materialism) => {
        if (materialism > 0.6) return { label: "Transactional (ROI)", desc: "Voters ask: 'What do I get?' Focus on tax value, investment, and tangible improvements.", icon: Coins };
        if (materialism < 0.4) return { label: "Idealistic (Values)", desc: "Voters ask: 'Is it right?' Focus on social good, community health, and ethics.", icon: Heart };
        return { label: "Balanced Economy", desc: "Voters weigh personal cost against public benefit.", icon: Scale };
    };

    const getPersonalityFilter = (openness, conscientiousness) => {
        if (openness > 0.6 && conscientiousness > 0.6) return { label: "The 'Innovator-Architect'", desc: "They want NEW ideas, but with SOLID execution plans." };
        if (openness > 0.6) return { label: "The 'Dreamer'", desc: "They respond to bold visions and novelty. 'Business as usual' bores them." };
        if (conscientiousness > 0.6) return { label: "The 'Traditionalist'", desc: "They value duty, reliability, and proven methods. Avoid risky experiments." };
        return { label: "The 'Generalist'", desc: "Moderate approach. Avoid extremes in both novelty and tradition." };
    };

    const motivation = getMotivationStrategy(psychographics.amygdala_response, psychographics.dlPFC_activation);
    const tribal = getTribalTone(psychographics.collective_narcissism, psychographics.sdo_score);
    const economic = getEconomicDriver(psychographics.materialism);
    const personality = getPersonalityFilter(psychographics.ocean_openness, psychographics.ocean_conscientiousness);
    const MotivationIcon = motivation.icon;

    // --- NEW: COGNITIVE & CIVIC INTERPRETER ---
    const getCognitiveStyle = (closure, needForCog) => {
        if (closure > 0.6) return { label: "Heuristic Processor", desc: "Prefers simple, decisive answers. Ambiguity creates anxiety. Use slogans and clear binary choices." };
        if (closure < 0.45) return { label: "Nuance Tolerant", desc: "Resists slogans. Seeks depth and complexity. Accepts that 'it's complicated'. Provide details." };
        return { label: "Balanced Processor", desc: "Needs a clear headline but willing to read the fine print if interested." };
    };

    const getCivicStance = (trust, engagement) => {
        if (trust > 0.6) return { label: "System Guardian", desc: "Believes the system works. Wants competence and reform, not revolution. Avoid 'burn it down' rhetoric." };
        if (trust < 0.4) return { label: "System Skeptic", desc: "Distrusts elites. Responds to outsider energy and anti-establishment framing." };
        return { label: "Cautious Citizen", desc: "Trust is conditional. Verify everything." };
    };

    const cognitive = getCognitiveStyle(psychographics.need_cognitive_closure, 0.5);
    const civic = getCivicStance(psychographics.institutional_trust, psychographics.political_profile?.political_engagement);


    // --- MORAL FOUNDATIONS HELPER ---
    const getMoralLabel = (key) => {
        const map = {
            mft_care_harm: "Care / Harm",
            mft_fairness_cheating: "Fairness / Cheating",
            mft_loyalty_betrayal: "Loyalty / Betrayal",
            mft_authority_subversion: "Authority / Subversion",
            mft_purity_sanctity: "Purity / Sanctity"
        };
        return map[key] || key.replace('mft_', '').replace('_', ' / ');
    };

    const sortedFoundations = Object.entries(psychographics)
        .filter(([k]) => k.startsWith('mft_'))
        .sort(([,a], [,b]) => b - a)
        .map(([k, v]) => ({ key: k, val: v, label: getMoralLabel(k) }));

    const topFoundations = sortedFoundations.slice(0, 2);
    const secondaryFoundations = sortedFoundations.slice(2);

    const getVoterSentiment = (trust, rwa) => {
        if (trust > 0.6 && rwa > 0.5) return { title: "Institutional Guardian", desc: "Trusts established systems; prefers order and hierarchy." };
        if (trust < 0.4 && rwa < 0.5) return { title: "System Sceptic", desc: "Sceptical of elites; prefers grassroots or anti-establishment approaches." };
        if (trust < 0.5 && rwa > 0.6) return { title: "Order Restoration", desc: "Low trust in current institutions but high desire for strict order." };
        return { title: "Pragmatic Moderate", desc: "Balanced trust; evaluates competence over ideology." };
    };

    const voterSentiment = getVoterSentiment(psychographics.institutional_trust || 0.5, psychographics.rwa_score || 0.5);

    return (
        <div className="space-y-5 animate-in fade-in slide-in-from-bottom-4 duration-500 pb-8">
             
             {/* 1. Psychographic Summary */}
             <section className="bg-slate-900/20 rounded-xl p-0.5 border border-slate-700/30 overflow-hidden">
                <RenderPsychographicSummary 
                    summary={psychographics} 
                    polarization={results?.polarizationAnalysis}
                />
            </section>

             {/* 2. VOTER RESONANCE STRATEGY */}
             <section>
                <div className="flex items-center gap-2 mb-3 px-1">
                    <Megaphone size={18} className="text-amber-400" />
                    <h3 className="text-xs font-bold text-slate-300 uppercase tracking-widest">Voter Resonance Strategy</h3>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 items-stretch">
                    
                    {/* LEFT: Primary Motivation */}
                    <div className={`p-5 rounded-xl border ${motivation.border} ${motivation.bg} relative overflow-hidden flex flex-col justify-between h-full`}>
                        <div className="relative z-10">
                            <div className="flex items-center justify-between mb-4">
                                <div>
                                    <div className="text-[10px] uppercase tracking-widest opacity-70 mb-1 font-bold text-slate-200">Primary Campaign Hook</div>
                                    <h2 className="text-2xl font-black text-white leading-none">{motivation.title}</h2>
                                </div>
                                <div className={`p-2.5 rounded-full bg-slate-900/50 ${motivation.color} border border-white/10`}>
                                    <MotivationIcon size={22} />
                                </div>
                            </div>
                            
                            <div className="bg-slate-900/40 rounded-lg p-3.5 border border-white/5 mb-4">
                                <div className="text-[10px] font-bold text-slate-300 mb-1 uppercase tracking-wide">How to Win Them</div>
                                <p className="text-sm text-slate-200 leading-relaxed font-medium">
                                    {motivation.desc}
                                </p>
                            </div>

                            <div className="flex flex-wrap gap-2">
                                <span className="px-3 py-1 rounded-full bg-slate-900/50 border border-white/10 text-[10px] text-slate-300 uppercase font-bold tracking-wide">
                                    Format: {motivation.hook}
                                </span>
                                {(psychographics.future_orientation || 0.5) > 0.6 && (
                                    <span className="px-3 py-1 rounded-full bg-indigo-900/50 border border-indigo-500/30 text-[10px] text-indigo-300 uppercase font-bold flex items-center gap-1 tracking-wide">
                                        <Clock size={10} /> Long-Term Focus
                                    </span>
                                )}
                            </div>
                        </div>
                        <div className={`absolute -right-10 -bottom-10 w-40 h-40 ${motivation.bg.replace('/10','/20')} rounded-full blur-3xl`}></div>
                    </div>

                    {/* RIGHT: Community & Drivers */}
                    <div className="flex flex-col gap-3 h-full">
                        <div className="bg-slate-900/40 border border-slate-700/50 rounded-xl p-4 flex flex-col justify-center shrink-0">
                            <div className="flex items-center gap-2 mb-2">
                                <Fingerprint size={16} className="text-slate-400" />
                                <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Community Tone</span>
                            </div>
                            <div className="text-lg font-bold text-white mb-1">"{tribal.label}"</div>
                            <p className="text-xs text-slate-300 leading-relaxed mb-3 font-medium">
                                {tribal.directive}
                            </p>
                            <div className="h-px bg-white/5 w-full my-2" />
                            <div className="flex justify-between items-center mt-1">
                                <span className="text-[9px] text-slate-400 uppercase tracking-widest font-bold">Top Value</span>
                                <span className="text-xs font-bold text-emerald-400">{sortedFoundations[0].label} ({(sortedFoundations[0].val * 100).toFixed(0)}%)</span>
                            </div>
                        </div>

                        <div className="grid grid-cols-2 gap-3 flex-1 min-h-[140px]">
                            <div className="bg-slate-900/40 border border-slate-700/50 rounded-xl p-4 flex flex-col justify-center h-full">
                                <div className="flex items-center gap-2 mb-2">
                                    <economic.icon size={14} className="text-yellow-400" />
                                    <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Economy</span>
                                </div>
                                <div className="text-sm font-bold text-white leading-tight mb-1">{economic.label}</div>
                                <p className="text-[10px] text-slate-400 leading-snug font-medium">{economic.desc}</p>
                            </div>
                            
                            <div className="bg-slate-900/40 border border-slate-700/50 rounded-xl p-4 flex flex-col justify-center h-full">
                                <div className="flex items-center gap-2 mb-2">
                                    <Sparkles size={14} className="text-purple-400" />
                                    <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Personality</span>
                                </div>
                                <div className="text-sm font-bold text-white leading-tight mb-1">{personality.label}</div>
                                <p className="text-[10px] text-slate-400 leading-snug font-medium">{personality.desc}</p>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* 3. [NEW SECTION] COGNITIVE & CIVIC ARCHITECTURE */}
            <section>
                <GlassContainer className="p-5">
                    <div className="flex items-center gap-3 mb-5">
                        <div className="p-2 bg-blue-500/10 rounded-lg border border-blue-500/20">
                            <Brain size={20} className="text-blue-400" />
                        </div>
                        <div>
                            <h3 className="text-lg font-bold text-slate-300">Cognitive & Civic Architecture</h3>
                            <p className="text-xs text-slate-400 font-medium">Information processing style and institutional relationship</p>
                        </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        
                        {/* 3A. Info Processing (Cognitive Closure) */}
                        <div className="bg-slate-950/30 p-4 rounded-xl border border-slate-800/50 flex flex-col justify-between">
                            <div>
                                <div className="flex items-center gap-2 mb-2">
                                    <BookOpen size={14} className="text-cyan-400" />
                                    <span className="text-[10px] font-bold text-cyan-400 uppercase tracking-widest">Info Processing</span>
                                </div>
                                <div className="text-sm font-bold text-white mb-2">{cognitive.label}</div>
                                <p className="text-[10px] text-slate-400 leading-relaxed font-medium">
                                    {cognitive.desc}
                                </p>
                            </div>
                            <div className="mt-3 flex items-center gap-2">
                                <span className="text-[9px] text-slate-500 font-mono">Nuance</span>
                                <div className="flex-1 h-1.5 bg-slate-800 rounded-full overflow-hidden">
                                    {/* Inverted: Lower closure = Higher nuance capacity */}
                                    <div className="h-full bg-cyan-500 rounded-full" style={{ width: `${(1 - psychographics.need_cognitive_closure) * 100}%` }}></div>
                                </div>
                                <span className="text-[9px] text-slate-500 font-mono">Simplicity</span>
                            </div>
                        </div>

                        {/* 3B. Threat vs Opportunity (Threat Sensitivity) */}
                        <div className="bg-slate-950/30 p-4 rounded-xl border border-slate-800/50 flex flex-col justify-between">
                            <div>
                                <div className="flex items-center gap-2 mb-2">
                                    <Zap size={14} className={psychographics.threat_sensitivity > 0.5 ? "text-rose-400" : "text-emerald-400"} />
                                    <span className={`text-[10px] font-bold uppercase tracking-widest ${psychographics.threat_sensitivity > 0.5 ? "text-rose-400" : "text-emerald-400"}`}>
                                        Emotional Driver
                                    </span>
                                </div>
                                <div className="text-sm font-bold text-white mb-2">
                                    {psychographics.threat_sensitivity > 0.5 ? "Threat Sensitive" : "Opportunity Driven"}
                                </div>
                                <p className="text-[10px] text-slate-400 leading-relaxed font-medium">
                                    {psychographics.threat_sensitivity > 0.5 
                                        ? "Motivated by preventing loss and ensuring safety. Frame policies as 'Protections'." 
                                        : "Motivated by gains and improvements. Fear tactics will fail. Frame as 'Upgrades'."}
                                </p>
                            </div>
                            <div className="mt-3 w-full bg-slate-800 rounded-full h-1.5 overflow-hidden relative">
                                <div className="absolute left-1/2 top-0 bottom-0 w-0.5 bg-white/20 z-10"></div>
                                <div 
                                    className={`h-full rounded-full ${psychographics.threat_sensitivity > 0.5 ? 'bg-rose-500' : 'bg-emerald-500'}`} 
                                    style={{ width: `${Math.abs((psychographics.threat_sensitivity - 0.5) * 200)}%`, marginLeft: psychographics.threat_sensitivity > 0.5 ? '50%' : `${50 - Math.abs((psychographics.threat_sensitivity - 0.5) * 200)}%` }}
                                ></div>
                            </div>
                        </div>

                        {/* 3C. Institutional Stance (Trust) */}
                        <div className="bg-slate-950/30 p-4 rounded-xl border border-slate-800/50 flex flex-col justify-between">
                            <div>
                                <div className="flex items-center gap-2 mb-2">
                                    <Landmark size={14} className="text-purple-400" />
                                    <span className="text-[10px] font-bold text-purple-400 uppercase tracking-widest">Civic Stance</span>
                                </div>
                                <div className="text-sm font-bold text-white mb-2">{civic.label}</div>
                                <p className="text-[10px] text-slate-400 leading-relaxed font-medium">
                                    {civic.desc}
                                </p>
                            </div>
                            <div className="mt-3 flex justify-between items-center text-[10px] font-mono text-slate-500 border-t border-slate-800 pt-2">
                                <span>Trust Score:</span>
                                <span className="text-white font-bold">{(psychographics.institutional_trust * 100).toFixed(0)}/100</span>
                            </div>
                        </div>

                    </div>
                </GlassContainer>
            </section>

            {/* 4. VALUE RESONANCE & MORAL FOUNDATIONS */}
            <section>
                <GlassContainer className="p-5">
                    <div className="flex items-center gap-3 mb-5">
                        <div className="p-2 bg-amber-500/10 rounded-lg border border-amber-500/20">
                            <Scale size={20} className="text-amber-400" />
                        </div>
                        <div>
                            <h3 className="text-lg font-bold text-slate-300">Value Resonance Engine</h3>
                            <p className="text-xs text-slate-400 font-medium">Moral intuition and sociopolitical drivers</p>
                        </div>
                    </div>

                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                        {/* LEFT: Moral Foundations */}
                        <div>
                            <h4 className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-3 border-b border-white/5 pb-2">
                                Dominant Moral Drivers
                            </h4>
                            <div className="space-y-3">
                                {topFoundations.map((foundation, i) => (
                                    <div key={i} className="bg-slate-950/50 p-3 rounded-lg border-l-2 border-amber-500 border-y border-r border-slate-800/50">
                                        <div className="flex justify-between items-center">
                                            <span className="text-sm font-bold text-slate-200">{foundation.label}</span>
                                            <span className="text-xs font-mono text-amber-500 font-bold">{(foundation.val * 100).toFixed(0)}% Impact</span>
                                        </div>
                                    </div>
                                ))}
                                <div className="mt-5">
                                    <h5 className="text-[9px] text-slate-400 uppercase tracking-wider mb-2 font-bold">Secondary Drivers</h5>
                                    {secondaryFoundations.map(f => (
                                        <div key={f.key} className="flex items-center gap-3 mb-2">
                                            <span className="text-[10px] text-slate-400 w-28 truncate font-medium">{f.label}</span>
                                            <div className="h-1.5 flex-1 bg-slate-800 rounded-full overflow-hidden">
                                                <div className="h-full bg-slate-600" style={{ width: `${(f.val || 0) * 100}%` }}></div>
                                            </div>
                                            <span className="text-[10px] text-slate-400 font-mono w-8 text-right font-bold">{(f.val * 100).toFixed(0)}%</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>

                        {/* RIGHT: Sociopolitical Positioning */}
                        <div>
                            <h4 className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-3 border-b border-white/5 pb-2">
                                Sociopolitical Profile
                            </h4>
                            <div className="bg-indigo-900/10 border border-indigo-500/20 p-4 rounded-xl mb-5 relative overflow-hidden">
                                <div className="absolute top-0 right-0 p-2 opacity-10">
                                    <Anchor size={48} />
                                </div>
                                <div className="relative z-10">
                                    <div className="text-[9px] text-indigo-300 uppercase tracking-widest mb-1 font-bold">Voter Sentiment</div>
                                    <h3 className="text-lg font-black text-white italic mb-1">"{voterSentiment.title}"</h3>
                                    <p className="text-xs text-indigo-200/80 leading-relaxed font-medium">
                                        {voterSentiment.desc}
                                    </p>
                                </div>
                            </div>

                            <div className="space-y-3">
                                {/* Trust */}
                                <div className="flex items-center justify-between p-3 bg-slate-950/30 rounded-lg border border-slate-800/50">
                                    <div className="flex items-center gap-3">
                                        <Shield size={14} className={psychographics.institutional_trust > 0.5 ? "text-emerald-400" : "text-rose-400"} />
                                        <div className="flex flex-col">
                                            <span className="text-xs font-bold text-slate-300">Institutional Trust</span>
                                            <span className="text-[9px] text-slate-400 font-medium">Confidence in established systems</span>
                                        </div>
                                    </div>
                                    <span className="font-mono text-xs font-bold text-slate-400">{(psychographics.institutional_trust * 100).toFixed(0)}%</span>
                                </div>
                                {/* Collectivism */}
                                <div className="flex items-center justify-between p-3 bg-slate-950/30 rounded-lg border border-slate-800/50">
                                    <div className="flex items-center gap-3">
                                        <Users size={14} className="text-cyan-400" />
                                        <div className="flex flex-col">
                                            <span className="text-xs font-bold text-slate-300">Social Orientation</span>
                                            <span className="text-[9px] text-slate-400 font-medium">{psychographics.collectivism_individualism > 0.5 ? "Individual-Centred" : "Group-Centred"}</span>
                                        </div>
                                    </div>
                                    <span className="font-mono text-xs font-bold text-slate-400">{(psychographics.collectivism_individualism * 100).toFixed(0)}%</span>
                                </div>
                                {/* Order */}
                                <div className="flex items-center justify-between p-3 bg-slate-950/30 rounded-lg border border-slate-800/50">
                                    <div className="flex items-center gap-3">
                                        <Eye size={14} className="text-purple-400" />
                                        <div className="flex flex-col">
                                            <span className="text-xs font-bold text-slate-300">Need for Order</span>
                                            <span className="text-[9px] text-slate-400 font-medium">Preference for hierarchy & structure</span>
                                        </div>
                                    </div>
                                    <span className="font-mono text-xs font-bold text-slate-400">{(psychographics.rwa_score * 100).toFixed(0)}%</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </GlassContainer>
            </section>

             {/* 5. Archetype Intelligence (Existing) */}
             {psychographics?.archetype_mix && (
                <section>
                    <GlassContainer className="p-5">
                        <div className="flex items-center gap-3 mb-5">
                            <div className="p-2 bg-indigo-500/10 rounded-lg border border-indigo-500/20">
                                <Users size={20} className="text-indigo-400" />
                            </div>
                            <div>
                                <h3 className="text-lg font-bold text-slate-300">Archetype Distribution</h3>
                                <p className="text-xs text-slate-400 font-medium">Voter persona targeting optimization</p>
                            </div>
                        </div>

                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                            {/* Archetype Breakdown */}
                            <div>
                                <h4 className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-3">Primary Archetypes</h4>
                                <div className="space-y-2">
                                    {Object.entries(psychographics.archetype_mix)
                                        .sort(([,a], [,b]) => b - a)
                                        .slice(0, 5)
                                        .map(([archetype, weight]) => {
                                            const percentage = weight > 1 ? weight : weight * 100;
                                            const colors = {
                                                'Security Seekers': 'from-red-500 to-orange-500',
                                                'Established Liberal Core': 'from-yellow-500 to-amber-500',
                                                'Professional Moderate': 'from-blue-500 to-cyan-500',
                                                'Soft-Left Liberal': 'from-pink-500 to-rose-500',
                                                'Calm Persistence': 'from-emerald-500 to-teal-500'
                                            };
                                            const color = colors[archetype] || 'from-slate-500 to-slate-600';

                                            return (
                                                <div key={archetype} className="bg-slate-950/30 p-2.5 rounded-lg border border-slate-800/50 flex items-center justify-between">
                                                    <div className="flex items-center gap-3">
                                                        <div className={`w-1.5 h-6 rounded bg-gradient-to-b ${color}`}></div>
                                                        <span className="text-xs font-bold text-slate-200 capitalize">{archetype.replace(/_/g, ' ')}</span>
                                                    </div>
                                                    <span className="font-mono text-xs font-bold text-slate-400">{percentage.toFixed(1)}%</span>
                                                </div>
                                            );
                                        })}
                                </div>
                            </div>

                            {/* Targeting Strategy */}
                            <div>
                                <h4 className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-3">Targeting Strategy</h4>
                                <div className="h-full">
                                    {(() => {
                                        const topArchetypeEntry = Object.entries(psychographics.archetype_mix).sort(([,a], [,b]) => b - a)[0];
                                        const topKey = topArchetypeEntry ? topArchetypeEntry[0].toLowerCase().replace(/ /g, '_') : 'general';
                                        
                                        const strategies = {
                                            'security_seekers': { m: 'Safety, Order, Protection', desc: 'Focus on stability and minimising risk.' },
                                            'established_liberal_core': { m: 'Community, Progress, Values', desc: 'Reinforce shared community values and steady progress.' },
                                            'professional_moderate': { m: 'Competence, Efficiency, Data', desc: 'Focus on pragmatic solutions and proven results.' },
                                            'tradition_oriented': { m: 'Heritage, Family, Continuity', desc: 'Respect tradition and highlight continuity.' },
                                            'compassion_driven': { m: 'Empathy, Care, Support', desc: 'Highlight human connection and social support.' },
                                            'justice_seekers': { m: 'Fairness, Equality, Reform', desc: 'Focus on systemic fairness and equality.' },
                                            'innovators': { m: 'Future, Tech, Change', desc: 'Emphasize new approaches and future potential.' },
                                            'pragmatists': { m: 'Results, Plans, Concrete Action', desc: 'Focus on tangible outcomes and detailed plans.' }
                                        };
                                        const strategy = strategies[topKey] || strategies['security_seekers']; 

                                        return (
                                            <div className="bg-slate-950/30 p-4 rounded-xl border border-slate-800/50 flex flex-col justify-center min-h-[140px]">
                                                <div className="mb-3">
                                                    <div className="text-[9px] text-indigo-400 uppercase tracking-widest mb-1 font-bold">Key Themes</div>
                                                    <p className="text-sm text-white font-bold leading-tight">{strategy.m}</p>
                                                </div>
                                                <div>
                                                    <div className="text-[9px] text-purple-400 uppercase tracking-widest mb-1 font-bold">Approach</div>
                                                    <p className="text-xs text-slate-300 font-medium leading-relaxed">{strategy.desc}</p>
                                                </div>
                                            </div>
                                        );
                                    })()}
                                </div>
                            </div>
                        </div>
                    </GlassContainer>
                </section>
             )}

             {/* 6. Demographic Representation Audit (Existing) */}
             {demographicAudit && (
                <section>
                    <GlassContainer className="p-5">
                        <div className="flex items-center gap-3 mb-5">
                            <div className="p-2 bg-teal-500/10 rounded-lg border border-teal-500/20">
                                <Users size={20} className="text-teal-400" />
                            </div>
                            <div>
                                <h3 className="text-lg font-bold text-slate-300">Demographic Representation</h3>
                                <p className="text-xs text-slate-400 font-medium">Visual diversity and inclusion analysis</p>
                            </div>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                            {/* Age */}
                            {demographicAudit.age_representation && (
                                <div className="bg-slate-950/30 p-4 rounded-lg border border-slate-800/50 flex flex-col justify-between">
                                    <div>
                                        <div className="flex items-center gap-2 mb-3">
                                            <Clock size={16} className="text-blue-400" />
                                            <h4 className="text-xs font-bold text-slate-300 uppercase tracking-wider">Age Coverage</h4>
                                        </div>
                                        <div className="space-y-2 mb-3">
                                            {Object.entries(demographicAudit.age_representation.distribution || {}).map(([age, count]) => (
                                                <div key={age} className="flex justify-between items-center text-xs">
                                                    <span className="text-slate-400">{age.replace('_', ' ')}</span>
                                                    <span className="font-mono text-white font-bold">{count} images</span>
                                                </div>
                                            ))}
                                        </div>
                                        {demographicAudit.age_representation.gaps?.length > 0 && (
                                            <div className="pt-2 border-t border-slate-800/50">
                                                <div className="text-[10px] text-orange-400 mb-1 flex items-center gap-1 font-bold uppercase">
                                                    <AlertTriangle size={10} /> Missing:
                                                </div>
                                                <div className="flex flex-wrap gap-1">
                                                    {demographicAudit.age_representation.gaps.map((gap, i) => (
                                                        <span key={i} className="px-2 py-0.5 bg-orange-900/20 text-orange-300 text-[9px] rounded border border-orange-800/30 uppercase font-bold tracking-wide">
                                                            {gap}
                                                        </span>
                                                    ))}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                    {demographicAudit.age_representation.recommendation && (
                                        <div className="mt-3 pt-2 border-t border-slate-800/50 text-[10px] text-blue-300 italic">
                                            "{demographicAudit.age_representation.recommendation}"
                                        </div>
                                    )}
                                </div>
                            )}
                            {/* Family */}
                            {demographicAudit.family_representation && (
                                <div className="bg-slate-950/30 p-4 rounded-lg border border-slate-800/50 flex flex-col justify-between">
                                    <div>
                                        <div className="flex items-center gap-2 mb-3">
                                            <Heart size={16} className="text-pink-400" />
                                            <h4 className="text-xs font-bold text-slate-300 uppercase tracking-wider">Family Units</h4>
                                        </div>
                                        <div className="space-y-2 mb-3">
                                            {Object.entries(demographicAudit.family_representation.distribution || {}).map(([type, count]) => (
                                                <div key={type} className="flex justify-between items-center text-xs">
                                                    <span className="text-slate-400 capitalize">{type.replace('_', ' ')}</span>
                                                    <span className="font-mono text-white font-bold">{count}</span>
                                                </div>
                                            ))}
                                        </div>
                                        {demographicAudit.family_representation.gaps?.length > 0 && (
                                            <div className="pt-2 border-t border-slate-800/50">
                                                <div className="text-[10px] text-orange-400 mb-1 flex items-center gap-1 font-bold uppercase">
                                                     <AlertTriangle size={10} /> Missing:
                                                </div>
                                                <div className="flex flex-wrap gap-1">
                                                    {demographicAudit.family_representation.gaps.map((gap, i) => (
                                                        <span key={i} className="px-2 py-0.5 bg-orange-900/20 text-orange-300 text-[9px] rounded border border-orange-800/30 uppercase font-bold tracking-wide">
                                                            {gap}
                                                        </span>
                                                    ))}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}
                            {/* Lifestyle */}
                            {demographicAudit.lifestyle_representation && (
                                <div className="bg-slate-950/30 p-4 rounded-lg border border-slate-800/50">
                                    <div className="flex items-center gap-2 mb-3">
                                        <Briefcase size={16} className="text-purple-400" />
                                        <h4 className="text-xs font-bold text-slate-300 uppercase tracking-wider">Lifestyle</h4>
                                    </div>
                                    <div className="space-y-2 mb-3">
                                        {Object.entries(demographicAudit.lifestyle_representation.distribution || {}).map(([type, count]) => (
                                            <div key={type} className="flex justify-between items-center text-xs">
                                                <span className="text-slate-400 capitalize">{type.replace('_', ' ')}</span>
                                                <span className="font-mono text-white font-bold">{count}</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                            {/* Accessibility */}
                            {demographicAudit.accessibility_representation && (
                                <div className="bg-slate-950/30 p-4 rounded-lg border border-slate-800/50 flex flex-col justify-between">
                                    <div>
                                        <div className="flex items-center gap-2 mb-3">
                                            <Shield size={16} className="text-green-400" />
                                            <h4 className="text-xs font-bold text-slate-300 uppercase tracking-wider">Accessibility</h4>
                                        </div>
                                        <div className="space-y-2 mb-3">
                                            {Object.entries(demographicAudit.accessibility_representation.distribution || {}).map(([type, count]) => (
                                                <div key={type} className="flex justify-between items-center text-xs">
                                                    <span className="text-slate-400 capitalize">{type.replace('_', ' ')}</span>
                                                    <span className="font-mono text-white font-bold">{count}</span>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                    {demographicAudit.accessibility_representation.recommendation && (
                                        <div className="mt-3 pt-2 border-t border-slate-800/50 text-[10px] text-green-300 flex items-start gap-1 italic">
                                            <CheckCircle2 size={10} className="mt-0.5 shrink-0" />
                                            "{demographicAudit.accessibility_representation.recommendation}"
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    </GlassContainer>
                </section>
             )}
        </div>
    );
};